import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import axios from "axios";
import { useUserData } from "./UserDataContext";

// Definicja interfejsu dla kontekstu autoryzacji
interface AuthContextType {
  isAuthenticated: boolean;
  handleLogin: (username: string, password: string) => Promise<void>;
  handleLogout: () => void;
}

// Tworzenie kontekstu z domyślną wartością undefined
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hak do korzystania z kontekstu autoryzacji
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Interfejs dla komponentu dostawcy kontekstu
interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { updateUserData } = useUserData();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    // Pobieranie tokenu z sessionStorage
    const token = sessionStorage.getItem("token");
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (username: string, password: string): Promise<void> => {
    if (!username || !password) {
      throw new Error("Username or password cannot be empty.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}login`,
        { email: username, password },
        { headers: { "Content-Type": "application/json" } }
      );

      const { userId, token } = response.data;

      // Pobierz dane użytkownika po zalogowaniu
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}usersGET/${userId}`
      );

      const userData = {
        id: userResponse.data.id,
        username: userResponse.data.username,
        foxessKey: userResponse.data.foxees_token,
        fusionSolarPassword: userResponse.data.fusionSolar_password,
        fusionSolarUser: userResponse.data.fusionSolar_user,
        goodweEmail: userResponse.data.goodwe_account_email,
        goodwePassword: userResponse.data.goodwe_password,
        goodwePowerStationId: userResponse.data.goodwe_powerStationId,
        growattPassword: userResponse.data.growatt_password,
        growattUser: userResponse.data.growatt_user,
        solarEdgeToken: userResponse.data.solarEdge_token,
        solarmanEmail: userResponse.data.solarman_email,
        solarmanPassword: userResponse.data.solarman_password,
        solarmanToken: userResponse.data.solarman_token,
      };

      // Aktualizacja danych w UserDataContext
      updateUserData(userData);

      // Ustaw stan zalogowania
      setIsAuthenticated(true);

      // Zapisanie tokenu w sessionStorage
      sessionStorage.setItem("token", token);
    } catch (error) {
      setIsAuthenticated(false);
      throw new Error("Login failed. Please check your credentials.");
    }
  };

  const handleLogout = (): void => {
    // Wylogowanie: czyszczenie stanu i sessionStorage
    setIsAuthenticated(false);
    updateUserData({
      id: "",
      username: "",
      solarEdgeToken: "",
      solarmanToken: "",
      solarmanEmail: "",
      solarmanPassword: "",
      goodweEmail: "",
      goodwePassword: "",
      goodwePowerStationId: "",
      foxessKey: "",
      growattUser: "",
      growattPassword: "",
      fusionSolarUser: "",
      fusionSolarPassword: "",
    });
    sessionStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, handleLogin, handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
